import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Heart } from 'lucide-react';

const Footer = () => {
  const { themeColors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  // The messages page is a full-height chat app; the marketing footer below it
  // makes the page taller than the screen (which causes the view to jump when
  // the text input is focused on mobile). Hide it there.
  if (location.pathname.startsWith('/messages')) {
    return null;
  }

  const footerStyle = {
    backgroundColor: themeColors.background,
    borderTop: `1px solid ${themeColors.border}`,
    marginTop: 'auto',
    paddingTop: '48px',
    paddingBottom: '24px'
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  };

  const columnsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '40px',
    marginBottom: '48px'
  };

  const columnStyle = {
    display: 'flex',
    flexDirection: 'column'
  };

  const brandColumnStyle = {
    ...columnStyle,
    maxWidth: '280px'
  };

  const brandTitleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: themeColors.text,
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '12px'
  };

  const taglineStyle = {
    color: themeColors.textSecondary,
    fontSize: '14px',
    lineHeight: '1.6'
  };

  const columnHeaderStyle = {
    color: themeColors.primary,
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '16px'
  };

  const linkListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  };

  const linkStyle = {
    color: themeColors.textSecondary,
    fontSize: '14px',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s'
  };

  const bottomBarStyle = {
    paddingTop: '24px',
    borderTop: `1px solid ${themeColors.border}`,
    textAlign: 'center'
  };

  const copyrightStyle = {
    color: themeColors.textSecondary,
    fontSize: '14px'
  };

  const responsiveStyle = `
    @media (max-width: 1024px) {
      .footer-columns {
        grid-template-columns: repeat(2, 1fr) !important;
      }
    }
    @media (max-width: 640px) {
      .footer-columns {
        grid-template-columns: 1fr !important;
        gap: 32px !important;
      }
      .footer-brand-column {
        max-width: 100% !important;
      }
    }
  `;

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <style>{responsiveStyle}</style>
      <footer style={footerStyle}>
        <div style={containerStyle}>
          {/* Footer Columns */}
          <div style={columnsContainerStyle} className="footer-columns">
            {/* Brand Column */}
            <div style={brandColumnStyle} className="footer-brand-column">
              <div style={brandTitleStyle}>
                <Heart size={20} fill={themeColors.primary} color={themeColors.primary} />
                BabyResell
              </div>
              <p style={taglineStyle}>
                The trusted marketplace for buying and selling pre-loved baby items.
              </p>
            </div>

            {/* Company Column */}
            <div style={columnStyle}>
              <h3 style={columnHeaderStyle}>Company</h3>
              <ul style={linkListStyle}>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/about')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    About Us
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/contact')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Contact
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/careers')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Careers
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/blog')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Blog
                  </span>
                </li>
              </ul>
            </div>

            {/* Support Column */}
            <div style={columnStyle}>
              <h3 style={columnHeaderStyle}>Support</h3>
              <ul style={linkListStyle}>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/help')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Help Center
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/safety')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Safety
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/community-guidelines')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Community Guidelines
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/seller-guide')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Seller Guide
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/buyer-guide')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Buyer Guide
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/measuring-guide')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    How to Measure
                  </span>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div style={columnStyle}>
              <h3 style={columnHeaderStyle}>Legal</h3>
              <ul style={linkListStyle}>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/terms')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Terms of Service
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/privacy')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Privacy Policy
                  </span>
                </li>
                <li>
                  <span
                    style={linkStyle}
                    onClick={() => handleNavigation('/cookies')}
                    onMouseOver={(e) => e.target.style.color = themeColors.primary}
                    onMouseOut={(e) => e.target.style.color = themeColors.textSecondary}
                  >
                    Cookie Policy
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div style={bottomBarStyle}>
            <p style={copyrightStyle}>
              &copy; {currentYear} BabyResell by Interverse. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
